/**
 * Admin category create/edit form (FEA-19, docs/FRONTEND-PLAN.md section 2.2).
 * Shared by Forum.jsx's "New category" and "Edit" actions, both of which are
 * gated on `role === 'admin'` - matching the backend's isForumAdmin gate
 * (admin role or forum:manage:all permission; the UI gates on role only).
 *
 * `POST /forum/categories` and `PUT /forum/categories/{categoryId}` accept
 * the identical body - {name, description, accreditedOnly, accessCriteria,
 * externallySearchable, sortOrder} - with PUT additionally taking `isActive`;
 * unknown keys 400. The accessCriteria editor is deliberately simple: a
 * verified-accreditation checkbox plus three comma-separated string lists
 * (allowedRoles / anyOfGroups / allOfGroups). Every stated criterion must
 * pass (AND semantics); an empty list is omitted entirely rather than sent
 * as `[]`, because the backend rejects a present-but-empty criteria array.
 */
import { useState } from 'react';
import { ErrorAlert, Field, TextareaField } from './ui';
import { cn, ui } from '../lib/ui';

function splitList(value) {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

export function emptyCategoryForm() {
  return {
    name: '',
    description: '',
    sortOrder: '0',
    accreditedOnly: false,
    externallySearchable: false,
    isActive: true,
    accessAccreditationVerified: false,
    allowedRoles: '',
    anyOfGroups: '',
    allOfGroups: ''
  };
}

export function categoryToForm(category) {
  const criteria = category.accessCriteria || {};
  return {
    name: category.name || '',
    description: category.description || '',
    sortOrder: String(category.sortOrder ?? 0),
    accreditedOnly: category.accreditedOnly === true,
    externallySearchable: category.externallySearchable === true,
    isActive: category.isActive !== false,
    accessAccreditationVerified: criteria.accreditation === 'verified',
    allowedRoles: Array.isArray(criteria.allowedRoles) ? criteria.allowedRoles.join(', ') : '',
    anyOfGroups: Array.isArray(criteria.anyOfGroups) ? criteria.anyOfGroups.join(', ') : '',
    allOfGroups: Array.isArray(criteria.allOfGroups) ? criteria.allOfGroups.join(', ') : ''
  };
}

/**
 * Build the request body. `includeIsActive` should be true only for edits -
 * create always starts active and the field isn't part of the create form.
 * Throws with a plain message on a non-integer sort order, for the caller to
 * surface as a form error.
 */
export function buildCategoryPayload(form, { includeIsActive = false } = {}) {
  const sortOrder = Number(form.sortOrder);
  if (!Number.isInteger(sortOrder)) {
    throw new Error('Sort order must be a whole number.');
  }

  const accessCriteria = {};
  if (form.accessAccreditationVerified) accessCriteria.accreditation = 'verified';
  const allowedRoles = splitList(form.allowedRoles);
  if (allowedRoles.length) accessCriteria.allowedRoles = allowedRoles;
  const anyOfGroups = splitList(form.anyOfGroups);
  if (anyOfGroups.length) accessCriteria.anyOfGroups = anyOfGroups;
  const allOfGroups = splitList(form.allOfGroups);
  if (allOfGroups.length) accessCriteria.allOfGroups = allOfGroups;

  const payload = {
    name: form.name.trim(),
    description: form.description.trim() || null,
    accreditedOnly: form.accreditedOnly === true,
    externallySearchable: form.externallySearchable === true,
    sortOrder,
    accessCriteria: Object.keys(accessCriteria).length ? accessCriteria : null
  };
  if (includeIsActive) {
    payload.isActive = form.isActive === true;
  }
  return payload;
}

/** `showIsActive` renders the isActive checkbox - pass it for edit, not create. */
export default function CategoryForm({ initial, submitLabel, showIsActive, onSubmit, onCancel }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  async function handleSubmit(event) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const payload = buildCategoryPayload(form, { includeIsActive: showIsActive });
      await onSubmit(payload);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <ErrorAlert error={error} />
      <Field label="Name" required value={form.name} onChange={(e) => set({ name: e.target.value })} />
      <TextareaField
        label="Description (optional)"
        rows={2}
        value={form.description}
        onChange={(e) => set({ description: e.target.value })}
      />
      <Field
        label="Sort order"
        type="number"
        step="1"
        required
        hint="Lower numbers list first."
        value={form.sortOrder}
        onChange={(e) => set({ sortOrder: e.target.value })}
      />
      <label className={cn(ui.row, 'text-body-s')}>
        <input
          type="checkbox"
          checked={form.accreditedOnly}
          onChange={(e) => set({ accreditedOnly: e.target.checked })}
        />{' '}
        Restrict to verified accredited investors
      </label>
      <label className={cn(ui.row, 'text-body-s')}>
        <input
          type="checkbox"
          checked={form.externallySearchable}
          onChange={(e) => set({ externallySearchable: e.target.checked })}
        />{' '}
        Show in search results for members who can&rsquo;t otherwise access this category
      </label>
      {showIsActive && (
        <label className={cn(ui.row, 'text-body-s')}>
          <input type="checkbox" checked={form.isActive} onChange={(e) => set({ isActive: e.target.checked })} />{' '}
          Active (visible to members who otherwise qualify)
        </label>
      )}

      <fieldset className="mt-4">
        <legend className={ui.label}>Access criteria (advanced admin tool)</legend>
        <p className={cn(ui.muted, 'text-body-s')}>
          Every criterion set below must pass (AND) for a non-staff member to see this category.
          Leave a list blank to skip it. Staff always bypass all criteria.
        </p>
        <label className={cn(ui.row, 'text-body-s')}>
          <input
            type="checkbox"
            checked={form.accessAccreditationVerified}
            onChange={(e) => set({ accessAccreditationVerified: e.target.checked })}
          />{' '}
          Require verified accreditation
        </label>
        <Field
          label="Allowed roles (optional, comma-separated)"
          hint="e.g. accredited-investor, employee, sales"
          value={form.allowedRoles}
          onChange={(e) => set({ allowedRoles: e.target.value })}
        />
        <Field
          label="Any of groups (optional, comma-separated)"
          hint="Member needs at least one of these membership groups."
          value={form.anyOfGroups}
          onChange={(e) => set({ anyOfGroups: e.target.value })}
        />
        <Field
          label="All of groups (optional, comma-separated)"
          hint="Member needs every one of these membership groups."
          value={form.allOfGroups}
          onChange={(e) => set({ allOfGroups: e.target.value })}
        />
      </fieldset>

      <div className={cn(ui.row, 'mt-4')}>
        <button type="submit" className={ui.btnPrimary} disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </button>
        {onCancel && (
          <button type="button" className={ui.btnSecondary} disabled={busy} onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
